<template>
  <div>
    <generate-codebox-code-modal ref="generateCodeboxCodeModal" />
    <addDevicesDialog
      ref="addDevicesDialog"
      @added="deviceAdded"
    />
    <div class="row full-row-exactly">
      <div class="col-12 flex-1">
        <div
          class="gutter-sm"
          :class="$q.screen.lt.md ? 'column' : 'row'"
        >
          <div
            v-if="$q.screen.lt.md || showFiltersDesktop"
            :class="$q.screen.lt.md && !showFiltersMobile ? 'col-auto' : 'col-md-3'"
          >
            <q-card :class="$q.screen.gt.sm ? 'full-height' : ''">
              <q-card-title>
                <div class="row">
                  <div class="col">
                    Filters
                  </div>
                  <div
                    v-if="$q.screen.lt.md"
                    class="col-auto q-mr-auto"
                  >
                    <q-btn
                      color="secondary"
                      flat
                      round
                      :icon="`keyboard_arrow_${showFiltersMobile ? 'up' :'down'}`"
                      @click="toggleFiltersMobile"
                    />
                  </div>
                </div>
              </q-card-title>
              <q-card-main
                v-if="$q.screen.gt.sm || showFiltersMobile"
                style="height: calc(100% - 64px)"
              >
                <component
                  :is="$q.screen.gt.sm ? 'q-scroll-area' : 'div'"
                  :class="{'full-height': $q.screen.gt.sm}"
                >
                  <bj-filters
                    ref="filtersModal"
                    :key="filtersResetReRenderKey"
                    v-model="filters"
                    :fields="filterFields"
                    @reset="filtersResetReRenderKey++"
                    @input="initFilters"
                  />
                </component>
              </q-card-main>
            </q-card>
          </div>
          <div class="col">
            <bj-data-table
              ref="dataTable"
              :with-cell-delete-button="false"
              add-button-label="Add Devices"
              :columns="columns"
              model="admin/devices"
              many-name="Devices"
              single-name="Device"
              :query-params="queryParams"
              filter-button
              :with-cell-edit-button="false"
              :with-cell-add-button="false"
              @add="openAddModal"
              @toggle-filters="toggleFiltersDesktop"
            >
              <template
                slot="cell-business"
                slot-scope="{row}"
              >
                <router-link :to="{ name: 'app.businesses.profile', params: { id: row.business_id } }">
                  {{ row.business.name }}
                </router-link>
              </template>
              <template
                slot="cell-provider"
                slot-scope="{row}"
              >
                {{ row.provider }}
              </template>
              <template
                slot="cell-serial-number"
                slot-scope="{row}"
              >
                {{ row.serial_number }}
              </template>
              <template
                slot="cell-unit"
                slot-scope="{row}"
              >
                <template v-if="(row.units && row.units.length) || row.unit">
                  <template v-for="(unit, idx) in (row.units || (row.unit ? [row.unit] : []))">
                    <router-link
                      :key="unit.id"
                      :to="{ name: 'app.properties.combined', params: { id: unit.property_id } }"
                    >
                      {{ unit.property ? `${unit.property.short_address} - ${unit.name || ''}`.trim() : '' }}
                    </router-link>
                    <span
                      v-if="idx < (row.units || (row.unit ? [row.unit] : [])).length - 1"
                      :key="`sep-${unit.id}`"
                    >, </span>
                  </template>
                </template>
              </template>
              <template
                slot="cell-status"
                slot-scope="{row}"
              >
                {{ row.device_status }}
              </template>
              <template
                slot="cell-actions"
                slot-scope="{row}"
              >
                <q-btn
                  v-if="(row.units && row.units.length) || row.unit"
                  v-tippy
                  size="sm"
                  rounded
                  outline
                  color="primary"
                  icon="fas fa-key"
                  title="Generate device code"
                  class="q-ml-sm"
                  @click.native="openGenerateCodeboxCodeModal(row)"
                />
                <q-btn
                  v-if="row.provider !== AccessHardware.CODEBOX"
                  v-tippy
                  size="sm"
                  rounded
                  outline
                  color="negative"
                  icon="delete"
                  title="Delete Device"
                  class="q-ml-sm"
                  @click.native="deleteDevice(row)"
                />
              </template>
            </bj-data-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import BjFilters from '@/components/filters.vue';
import BjDataTable from '@/plugins/bluejay/components/data-table.vue';
import GenerateCodeboxCodeModal from '@/components/generate-codebox-code-modal.vue';
import { AccessHardware } from '@/constants/AccessHardware';
import AddDevicesDialog from '@/pages/app/devices/addDevicesDialog.vue';
import BjAddInquiryModal from '@/components/add-inquiry-modal.vue';

export default {
  components: {
    BjAddInquiryModal,
    AddDevicesDialog,
    GenerateCodeboxCodeModal,
    BjDataTable,
    BjFilters,
  },
  data() {
    return {
      queryParams: {
        id: this.$route.params.id ? [this.$route.params.id] : null,
      },
      showFiltersMobile: false,
      showFiltersDesktop: false,
      filtersResetReRenderKey: 1,
      filters: {
        businesses: [],
        provider: [],
      },
      filterFields: [
        'businesses',
        'provider',
      ],
      columns: [
        {
          name: 'id',
          field: 'id',
          label: 'ID',
          sortable: true,
        },
        {
          name: 'business',
          field: 'business',
          label: 'Business',
          sortable: false,
        },
        {
          name: 'provider',
          field: 'provider',
          label: 'Provider',
          sortable: true,
        },
        {
          name: 'serial_number',
          field: 'serial_number',
          label: 'Serial number',
          sortable: false,
        },
        {
          name: 'unit',
          field: 'unit',
          label: 'Assigned unit',
          sortable: false,
        },
        {
          name: 'device_status',
          field: 'device_status',
          label: 'Status',
          sortable: true,
        },
      ],
      addDevicesDialog: this.clone({
        visible: false,
        loading: false,
      }),
      editPayload: null,
      disabled: false,
    };
  },
  computed: {
    AccessHardware() {
      return AccessHardware;
    },
  },
  created() {
    [this.queryParams, this.filters] = this.applySortingAndFiltering(this.queryParams, this.filters);
  },
  methods: {
    deviceAdded() {
      if (this.$refs.dataTable) {
        this.$refs.dataTable.refresh();
      }
    },
    openAddModal() {
      this.$refs.addDevicesDialog.show();
    },
    toggleFiltersMobile() {
      this.showFiltersMobile = !this.showFiltersMobile;
    },
    toggleFiltersDesktop() {
      this.showFiltersDesktop = !this.showFiltersDesktop;
    },
    openGenerateCodeboxCodeModal(device) {
      const unit = (device.units && device.units[0]) || device.unit;
      this.$refs.generateCodeboxCodeModal.show(unit);
    },
    deleteDevice(device) {
      this.uiConfirm('Are you sure you want to delete this device?').then(() => {
        this.api()
          .request('DELETE', `admin/devices/${device.id}`)
          .then(() => {
            this.$refs.dataTable.refresh();
            this.uiNotify('Device deleted successfully');
          }).catch((error) => {
            this.uiNotify(error.message || 'Error occurred', 'negative');
          });
      }).catch(() => {
      });
    },
    initFilters() {
      Object.keys(this.filters).forEach((key) => {
        const filter = this.clone(this.filters[key]);

        if (filter && filter.length) {
          this.queryParams[key] = filter;
        } else {
          delete this.queryParams[key];
        }
      });
      setTimeout(() => {
        this.$refs.dataTable.refresh();
      }, 100);
    },
  },
};

</script>
